import type { OrganizationType } from '@form-factory/schema';
import { Currency, Role } from '@form-factory/schema/enums';
import { formatUtils } from '@form-factory/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, toast } from '@utima/ui';
import { Form, Input, type TypedFormState } from '@utima/ui-informed';
import { z } from 'zod';

import { Card } from '@/components/card/Card';
import { Protected } from '@/components/protected/Protected';
import { trpc, trpcUtils } from '@/services/trpc';

const addBalanceSchema = z.object({
  amountToAdd: z
    .number({ invalid_type_error: 'Must be a number' })
    .positive('Amount must be positive'),
});

type AddBalanceFormValues = z.infer<typeof addBalanceSchema>;

export type AddBalanceFormProps = {
  data: OrganizationType;
};

export function AddBalanceForm({ data }: AddBalanceFormProps) {
  const { t } = useLingui();

  const currencySymbol = data.currency === Currency.CZK ? 'Kč' : '$';

  const addBalanceMutation = trpc.subscriptions.addManualBalance.useMutation({
    onSuccess: ({ newBalance }) => {
      toast.success(t`Success`, {
        description: t`Successfully added credit. New balance: $${newBalance.toFixed(2)}`,
      });
      trpcUtils.organizations.findOne.invalidate({ id: data.id });
    },
    onError: error => {
      toast.error(t`Error`, {
        description:
          error.message || t`Failed to add credit. Please try again.`,
      });
    },
  });

  const handleAddBalanceSubmit = (
    formState: TypedFormState<AddBalanceFormValues>,
  ) => {
    const values = formState.values;
    const validation = addBalanceSchema.safeParse(values);

    if (!validation.success) {
      toast.error(t`Validation Error`, {
        description: t`Please enter a valid positive amount.`,
      });

      return;
    }

    addBalanceMutation.mutate({
      organizationId: data.id,
      amountToAdd: validation.data.amountToAdd,
      reason: 'Manual credit addition via Admin UI',
      currency: data.currency,
    });
  };

  return (
    <Protected roles={[Role.SuperAdmin]}>
      <Card className='my-6' title={<Trans>Add Manual Credit</Trans>}>
        <p className='text-sm text-muted-fg'>
          <Trans>
            Add funds directly to the organization&apos;s prepaid balance. This
            is for manual adjustments or compensations. Current Balance:{' '}
            {typeof data.balance === 'number'
              ? formatUtils.credits(data.balance, data.currency)
              : `N/A`}
          </Trans>
        </p>

        <Form<AddBalanceFormValues>
          onSubmit={handleAddBalanceSubmit}
          zodSchema={addBalanceSchema}
          className='mt-4 flex items-end gap-4'
        >
          <div className='grow'>
            <Input
              label={t`Amount to Add (${currencySymbol})`}
              id='manualAmountToAdd'
              name='amountToAdd'
              required
              addonBefore={currencySymbol}
              type='number'
              step='0.01'
              placeholder='e.g., 10.00'
            />
          </div>

          <Button
            data-cta='admin-add-balance-form-add-button'
            type='submit'
            disabled={addBalanceMutation.isPending}
            className='whitespace-nowrap'
          >
            {addBalanceMutation.isPending ? (
              <Trans>Adding...</Trans>
            ) : (
              <Trans>Add Credit</Trans>
            )}
          </Button>
        </Form>
      </Card>
    </Protected>
  );
}
